import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Minus, Maximize2 } from 'lucide-react';
import { BRAZIL_STATE_SHAPES, MAP_VIEWBOX } from '@/data/brazilGeo';
import { stateName } from '@/data/states';
import { buildMapLayers, curvePath } from '@/lib/geo';
import { useReducedMotionPref } from '@/hooks/useMediaQuery';
import { useIsVisitor, VisitorOnly } from '@/hooks/useIsVisitor';
import { cn } from '@/lib/utils';

const SIZE = 1000;
const MIN_K = 1;
const MAX_K = 4;
const SMALL_UFS = new Set(['DF', 'SE', 'AL', 'PB', 'RN', 'ES', 'RJ']);
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const clampView = ({ k, x, y }) => {
  const kk = clamp(k, MIN_K, MAX_K);
  return { k: kk, x: clamp(x, SIZE - SIZE * kk, 0), y: clamp(y, SIZE - SIZE * kk, 0) };
};

/**
 * Mapa holográfico do Brasil (SVG + CSS). 27 UFs reais, pontos em cidades reais,
 * conexões entre oportunidades e regiões de interesse, zoom/pan (botões, Ctrl+roda, pinça).
 */
export const BrazilMapHolo = ({ companies, opportunities, selectedUf, onSelectUf, className }) => {
  const reduced = useReducedMotionPref();
  const isVisitor = useIsVisitor();
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const pathRefs = useRef({});
  const pointers = useRef(new Map());
  const gesture = useRef({ moved: false, start: null, view: null, pinch: null });
  const [view, setView] = useState({ k: 1, x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [hover, setHover] = useState(null);

  const { points, stateStats, connections } = useMemo(() => buildMapLayers(companies, opportunities), [companies, opportunities]);
  const maxCompanies = useMemo(() => Math.max(1, ...Object.values(stateStats).map((s) => s.companies)), [stateStats]);

  // Enquadra o estado selecionado
  useEffect(() => {
    if (!selectedUf) {
      setView({ k: 1, x: 0, y: 0 });
      return;
    }
    const el = pathRefs.current[selectedUf];
    if (!el) return;
    const b = el.getBBox();
    const k = clamp(Math.min(560 / b.width, 560 / b.height), 1, 2.6);
    const cx = b.x + b.width / 2;
    const cy = b.y + b.height / 2;
    setView(clampView({ k, x: SIZE / 2 - cx * k, y: SIZE / 2 - cy * k }));
  }, [selectedUf]);

  const toSvg = useCallback((clientX, clientY) => {
    const r = svgRef.current.getBoundingClientRect();
    return { x: ((clientX - r.left) / r.width) * SIZE, y: ((clientY - r.top) / r.height) * SIZE };
  }, []);

  const zoomAt = useCallback((factor, p = { x: SIZE / 2, y: SIZE / 2 }, base) => {
    setView((v0) => {
      const v = base || v0;
      const k = clamp(v.k * factor, MIN_K, MAX_K);
      const wx = (p.x - v.x) / v.k;
      const wy = (p.y - v.y) / v.k;
      return clampView({ k, x: p.x - wx * k, y: p.y - wy * k });
    });
  }, []);

  // Ctrl/⌘ + roda (e pinça de trackpad) para zoom, sem sequestrar a rolagem da página
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      zoomAt(e.deltaY < 0 ? 1.15 : 1 / 1.15, toSvg(e.clientX, e.clientY));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt, toSvg]);

  const onPointerDown = (e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    gesture.current = { moved: false, start: { x: e.clientX, y: e.clientY }, view, pinch: null };
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current.pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), mid: toSvg((a.x + b.x) / 2, (a.y + b.y) / 2), view };
    }
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (pointers.current.size === 2 && g.pinch) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      g.moved = true;
      setDragging(true);
      zoomAt(dist / g.pinch.dist, g.pinch.mid, g.pinch.view);
      return;
    }
    const dx = e.clientX - g.start.x;
    const dy = e.clientY - g.start.y;
    if (!g.moved && Math.hypot(dx, dy) < 5) return;
    if (g.view.k <= 1.01) return; // sem zoom: deixa a página rolar normalmente
    if (!g.moved) {
      g.moved = true;
      setDragging(true);
      try { svgRef.current.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
    }
    const r = svgRef.current.getBoundingClientRect();
    setView(clampView({ k: g.view.k, x: g.view.x + (dx / r.width) * SIZE, y: g.view.y + (dy / r.height) * SIZE }));
  };
  const endPointer = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) setTimeout(() => setDragging(false), 0);
  };

  const handleSelect = (uf) => {
    if (gesture.current.moved) return;
    onSelectUf?.(uf === selectedUf ? null : uf);
  };

  const handleHover = (e, uf) => {
    const r = wrapRef.current.getBoundingClientRect();
    setHover({ uf, x: e.clientX - r.left, y: e.clientY - r.top });
  };

  const k = view.k;
  const tilted = !reduced && k <= 1.01;
  const visibleConnections = selectedUf ? connections.filter((c) => c.fromUf === selectedUf || c.toUf === selectedUf) : connections.slice(0, 18);

  return (
    <div
      ref={wrapRef}
      className={cn('relative overflow-hidden rounded-[calc(var(--radius)+6px)] border border-white/10 bg-[#080b10] shadow-[0_18px_60px_rgba(0,0,0,0.55)] noise', className)}
      data-testid="brazil-map"
    >
      <div className="pointer-events-none absolute inset-0 grid-floor opacity-60 [mask-image:radial-gradient(ellipse_at_center,black_35%,transparent_75%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(520px_360px_at_55%_45%,rgba(34,211,238,0.10),transparent_70%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1/4 map-scan" />

      <div className="map-stage relative mx-auto w-full max-w-[min(100%,74vh)] px-2 pt-10 pb-14 sm:px-6">
        <div className="map-tilt" style={{ transform: tilted ? 'rotateX(17deg) rotateZ(-1deg) scale(0.97)' : 'rotateX(0deg)' }}>
          <svg
            ref={svgRef}
            viewBox={MAP_VIEWBOX}
            className="block aspect-square w-full select-none overflow-visible"
            style={{ touchAction: k > 1.01 ? 'none' : 'pan-y' }}
            role="group"
            aria-label="Mapa interativo do Brasil. Selecione um estado para ver empresas e oportunidades."
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPointer}
            onPointerCancel={endPointer}
            onPointerLeave={(e) => { endPointer(e); setHover(null); }}
          >
            <defs>
              <filter id="holo-glow" x="-10%" y="-10%" width="120%" height="120%">
                <feGaussianBlur stdDeviation="7" />
              </filter>
              <radialGradient id="marker-core">
                <stop offset="0%" stopColor="#e0fbff" />
                <stop offset="60%" stopColor="#22d3ee" />
                <stop offset="100%" stopColor="#0891b2" />
              </radialGradient>
            </defs>
            <g className={cn('map-zoom-layer', dragging && 'is-dragging')} style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${k})` }}>
              {/* profundidade: camadas extrudadas */}
              <g aria-hidden="true" transform={`translate(0 ${14 / k})`}>
                {BRAZIL_STATE_SHAPES.map((s) => <path key={s.uf} d={s.d} fill="rgba(6,30,44,0.85)" stroke="rgba(34,211,238,0.10)" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />)}
              </g>
              <g aria-hidden="true" transform={`translate(0 ${7 / k})`}>
                {BRAZIL_STATE_SHAPES.map((s) => <path key={s.uf} d={s.d} fill="rgba(8,40,58,0.9)" stroke="rgba(34,211,238,0.14)" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />)}
              </g>
              <g aria-hidden="true" filter="url(#holo-glow)" opacity={k > 1.01 ? 0.18 : 0.55}>
                {BRAZIL_STATE_SHAPES.map((s) => <path key={s.uf} d={s.d} fill="none" stroke="#22d3ee" strokeWidth="3" />)}
              </g>

              {/* estados */}
              <g>
                {BRAZIL_STATE_SHAPES.map((s) => {
                  const st = stateStats[s.uf];
                  const intensity = st ? 0.05 + 0.13 * (st.companies / maxCompanies) : 0.02;
                  const selected = selectedUf === s.uf;
                  return (
                    <path
                      key={s.uf}
                      ref={(el) => { pathRefs.current[s.uf] = el; }}
                      d={s.d}
                      className={cn('uf-shape', selected && 'is-selected', selectedUf && !selected && 'is-dim')}
                      style={{ '--uf-fill': `rgba(34,211,238,${intensity.toFixed(3)})` }}
                      tabIndex={0}
                      role="button"
                      aria-pressed={selected}
                      aria-label={`${stateName(s.uf)}: ${st?.companies || 0} empresas e ${st?.opportunities || 0} oportunidades`}
                      data-testid={`map-state-${s.uf}`}
                      onClick={() => handleSelect(s.uf)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSelectUf?.(s.uf === selectedUf ? null : s.uf);
                        }
                      }}
                      onPointerMove={(e) => e.pointerType === 'mouse' && handleHover(e, s.uf)}
                      onPointerLeave={() => setHover(null)}
                    />
                  );
                })}
              </g>

              {/* conexões: oportunidade → regiões de interesse */}
              <g aria-hidden="true" pointerEvents="none">
                {visibleConnections.map((c) => (
                  <path key={c.id} d={curvePath(c.from, c.to)} className={cn('map-connection', c.hot && 'is-hot')} style={{ opacity: selectedUf ? 0.95 : 0.55 }} />
                ))}
              </g>

              {/* rótulos UF */}
              <g aria-hidden="true" pointerEvents="none">
                {BRAZIL_STATE_SHAPES.map((s) => (SMALL_UFS.has(s.uf) && k < 1.8 && selectedUf !== s.uf ? null : (
                  <text key={s.uf} x={s.cx} y={s.cy + 22 / k} textAnchor="middle" fontSize={11 / k} fontFamily="IBM Plex Mono, monospace" letterSpacing="0.08em" fill={selectedUf === s.uf ? '#a5f3fc' : 'rgba(226,240,255,0.45)'}>
                    {s.uf}
                  </text>
                )))}
              </g>

              {/* marcadores */}
              <g pointerEvents="none">
                {points.map((p) => {
                  const n = p.companies.length;
                  const dim = selectedUf && p.uf !== selectedUf;
                  return (
                    <g key={p.id} opacity={dim ? 0.35 : 1} data-testid="map-marker">
                      {n > 0 && (
                        <>
                          <circle cx={p.x} cy={p.y} r={(5 + Math.min(4, n)) / k} fill="rgba(34,211,238,0.35)" className="marker-pulse" />
                          <circle cx={p.x} cy={p.y} r={(3.4 + Math.min(3, n) * 0.8) / k} fill="url(#marker-core)" style={{ filter: 'drop-shadow(0 0 6px rgba(34,211,238,0.8))' }} />
                        </>
                      )}
                      {p.recentOpps.length > 0 && (
                        <>
                          <circle cx={p.x + (n ? 8 / k : 0)} cy={p.y - (n ? 8 / k : 0)} r={5 / k} fill="rgba(255,59,92,0.4)" className="marker-pulse" style={{ animationDelay: '0.8s' }} />
                          <circle cx={p.x + (n ? 8 / k : 0)} cy={p.y - (n ? 8 / k : 0)} r={2.6 / k} fill="#ff3b5c" style={{ filter: 'drop-shadow(0 0 6px rgba(255,59,92,0.9))' }} />
                        </>
                      )}
                    </g>
                  );
                })}
              </g>
            </g>
          </svg>
        </div>
      </div>

      {/* tooltip */}
      {hover && !dragging && (
        <div
          className="pointer-events-none absolute z-10 rounded-md border border-white/10 bg-[#0b0f16] px-3 py-2 text-xs shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
          style={{ left: Math.min(hover.x + 14, (wrapRef.current?.clientWidth || 400) - 190), top: hover.y + 14 }}
          data-testid="map-tooltip"
        >
          <div className="font-display text-sm font-semibold text-foreground">{stateName(hover.uf)}</div>
          <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
            {stateStats[hover.uf]?.companies || 0} empresas · {stateStats[hover.uf]?.opportunities || 0} oportunidades
          </div>
          <VisitorOnly>
            <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground/80">exemplos fictícios</div>
          </VisitorOnly>
        </div>
      )}

      {/* controles */}
      <div className="absolute right-3 top-3 flex flex-col overflow-hidden rounded-md border border-white/10 bg-[#0b0f16]">
        {[
          { icon: Plus, label: 'Aproximar', id: 'map-zoom-in-button', fn: () => zoomAt(1.4) },
          { icon: Minus, label: 'Afastar', id: 'map-zoom-out-button', fn: () => zoomAt(1 / 1.4) },
          { icon: Maximize2, label: 'Visualização completa', id: 'map-reset-button', fn: () => { setView({ k: 1, x: 0, y: 0 }); onSelectUf?.(null); } },
        ].map(({ icon: Icon, label, id, fn }) => (
          <button key={id} type="button" onClick={fn} aria-label={label} title={label} data-testid={id} className="grid h-9 w-9 place-items-center border-b border-white/10 text-muted-foreground transition-colors last:border-b-0 hover:bg-white/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Icon className="h-4 w-4" />
          </button>
        ))}
      </div>

      <div className="absolute left-3 top-3 rounded-md border border-white/10 bg-[#0b0f16] px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        Mapa do ecossistema · {isVisitor
          ? <span className="text-primary">demo</span>
          : <span className="text-primary">ao vivo</span>}
      </div>

      {/* legenda */}
      <div className="absolute bottom-3 left-3 right-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-md border border-white/10 bg-[rgba(7,8,11,0.9)] px-3 py-2 text-[11px] text-muted-foreground sm:right-auto" data-testid="map-legend">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-cyan-300 shadow-[0_0_8px_rgba(34,211,238,0.9)]" />Empresas</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#ff3b5c] shadow-[0_0_8px_rgba(255,59,92,0.9)]" />Oportunidades recentes (7 dias)</span>
        <span className="flex items-center gap-1.5"><span className="h-px w-4 border-t border-dashed border-cyan-300" />Regiões de interesse</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-3 rounded-sm bg-cyan-400/30" />Densidade de empresas</span>
      </div>
    </div>
  );
};